// Filters dashboard list rows (Completed / Waiting) without affecting week aggregates.
import { getTaskProjects } from '@/features/tasks/utils/data/taskTags';

/**
 * @param {Task[]|null|undefined} tasks
 * @param {Set<string>} filteredTaskIds - ids from TasksContext.filteredTasks (chip filters)
 * @returns {Task[]}
 */
export function filterByChipFilters(tasks, filteredTaskIds) {
  if (!tasks?.length) return [];
  return tasks.filter((task) => task?.id && filteredTaskIds.has(task.id));
}

/**
 * @param {Task[]|null|undefined} tasks
 * @param {string|null|undefined} projectName - display name from ProjectBreakdown drill-down
 * @param {Map<string, string>|undefined} projectDisplayMap
 * @returns {Task[]}
 */
export function filterByBreakdownProject(
  tasks,
  projectName,
  projectDisplayMap,
) {
  if (!projectName || !tasks?.length) return tasks || [];
  return tasks.filter((task) => {
    const names = getTaskProjects(task).map(
      (pid) => projectDisplayMap?.get(pid) || pid,
    );
    return names.includes(projectName);
  });
}

/**
 * @param {Task[]|null|undefined} tasks
 * @param {{
 *   filteredTaskIds: Set<string>,
 *   breakdownProjectFilter?: string|null,
 *   projectDisplayMap?: Map<string, string>,
 * }} options
 * @returns {Task[]}
 */
export function applyDashboardListFilters(
  tasks,
  { filteredTaskIds, breakdownProjectFilter, projectDisplayMap },
) {
  return filterByBreakdownProject(
    filterByChipFilters(tasks, filteredTaskIds),
    breakdownProjectFilter,
    projectDisplayMap,
  );
}

/**
 * Voltooid tab badge: respects FilterBar chip filters and optional project drill-down.
 * FilterBar chip counts themselves stay stable; only this dashboard badge reacts.
 *
 * @param {{ tasks?: Task[] }[]|null|undefined} completedDays
 * @param {{
 *   breakdownProjectFilter?: string|null,
 *   filteredTaskIds: Set<string>,
 *   projectDisplayMap?: Map<string, string>,
 * }} options
 * @returns {number}
 */
export function countCompletedTabBadge(
  completedDays,
  { breakdownProjectFilter, filteredTaskIds, projectDisplayMap },
) {
  return (completedDays ?? []).reduce((total, day) => {
    const tasks = applyDashboardListFilters(day.tasks, {
      filteredTaskIds,
      breakdownProjectFilter,
      projectDisplayMap,
    });
    return total + tasks.length;
  }, 0);
}
